import { Injectable } from '@angular/core';
import { params } from '@app/configurations';
import { ApiService } from '@app/core/api/api.service';
import { ApiAccessLevel, ApiEndpoint } from '@app/core/models/api.models';
import { GlobalFilterType } from '@app/core/models/common.models';
import { SubFiltersService } from '@app/core/services/subfilters.service';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { GlobalFilterNotFound } from '@app/core/store/states/global-filters/global-filters-state.actions';
import { encodeSearchTerms } from '@app/core/utilities/url.utilities';
import { ForcedFilter } from '@app/shared/components';
import { defaultToastError } from '@app/shared/operators/default-toast-error.operator';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { ListItem } from '../models/selector.model';
import { BaseSelectorService } from './base-selector.service';
import { AccountDTO, ConvertCase, getAccountFilters } from './global-filters.utils';

@Injectable()
export class AccountSelectorService extends BaseSelectorService {
  requiredParams = [params.ADSPERT_ID, params.GLOBAL_FILTER_ACCOUNT_QUERY, null, null];
  includedData: any = [
    'customerId',
    'optimize',
    'platformAccessStatus',
    'adspertAccountId',
    'accountId',
    'accountName',
    'platform',
    'tagsUser',
    'signupCompleted',
  ];
  defaultLabel = 'global-filter.account-selector.placeholder';
  defaultSubfiltersType = 'accountsDefaultFilter';
  dto = AccountDTO;

  readonly srcSelectedItem$ = this.getSelectedItemStream(params.ADSPERT_ID);

  readonly subfilterOptions = () => this.subFiltersService.getAccountSubFilters();

  getSelectedItem(id): Observable<ListItem> {
    if (this.cache.items?.[id]) return of(this.cache.items[id]);
    this.loader$.next(true);
    return this.apiService
      .getObject({
        endpoint: ApiEndpoint.Accounts,
        include: this.includedData,
        route: [id],
      })
      .pipe(
        map(({ data }: any) => {
          this.loader$.next(false);
          this.cache.items[data.adspertAccountId] = this.dto(data);
          return this.cache.items[data.adspertAccountId];
        }),
        defaultToastError('Failed to fetch selected Accounts in the Global Filter. Please try with another selection.'),
        catchError((err) => {
          this.store.dispatch(new GlobalFilterNotFound(GlobalFilterType.ACCOUNTS));
          this.loader$.next(false);
          return of({});
        }),
      );
  }

  getItems(
    { query, filters = [], forMainCustomer },
    offset = 0,
    forDestination?: boolean,
    forcedFilters?: ForcedFilter,
  ): Observable<{ items: ListItem[]; count: number }> {
    this.listLoader$.next(true);
    const customerId = this.store.selectSnapshot(CustomerState.customerId);

    if (!!customerId) filters.push([forMainCustomer ? 'filter_main_customer_id' : 'filter_customer_id', customerId]);

    const refiners: any = {
      sortValues: { adspertAccountId: 1, accountName: 2 },
      filterValues: {
        ...ConvertCase(getAccountFilters(this.filterDefaults, {}, [...filters])),
        ...this.getForcedFilters(forcedFilters, forDestination),
      },
    };

    if (!!query) {
      refiners.searchFields = 'accountName,accountId,tagsUser';
      refiners.searchTerm = encodeSearchTerms(query);
    }

    return this.apiService
      .getList({
        endpoint: ApiEndpoint.Accounts,
        include: this.includedData,
        ...refiners,
        searchValues: this.getSearchValues(filters),
        limit: this.limit,
        offset: offset,
        accessLevel: forMainCustomer ? ApiAccessLevel.AgencyAdmin : ApiAccessLevel.User,
      })
      .pipe(
        tap(() => this.listLoader$.next(false)),
        map(({ data, meta }) => {
          const items = data.map(this.dto);
          return { items, count: meta.pagination.total };
        }),
        defaultToastError('Failed to fetch Accounts. Please try with another selection.'),
        catchError((err) => {
          this.listLoader$.next(false);
          return of({ items: [], count: 0 });
        }),
      );
  }

  constructor(
    translate: TranslateService,
    store: Store,
    apiService: ApiService,
    private subFiltersService: SubFiltersService,
  ) {
    super(translate, store, apiService);
  }
}
